import {
  EuiFormRow,
  EuiInputPopover,
  EuiSelectable,
  EuiSelectableOption,
} from "@inscopix/ideas-eui";
import { chain, get } from "lodash";
import { useToolSpecContext } from "providers/ToolSpecProvider/ToolSpecProvider";
import { useEffect, useMemo, useState } from "react";
import { ToolStatus } from "types/constants";
import { ToolSpec } from "types/json-schemas/type_guards";
import { uuid } from "utils/uuid";

export type FieldToolProps = {
  autoFocus?: boolean;
  label?: string;

  value: ToolSpec | undefined;
  onChange: (
    toolName: string | undefined,
    toolSpec: ToolSpec | undefined,
    toolVersionId: number | undefined,
  ) => void;
};

const NO_TOOLKIT = uuid();

export const FieldTool = ({
  autoFocus = false,
  label = "Analysis",
  value,
  onChange,
}: FieldToolProps) => {
  const { availableToolSpecs } = useToolSpecContext();

  const initialOptions: EuiSelectableOption[] = useMemo(() => {
    const validSpecs = availableToolSpecs
      // don't show deprecated tools as options
      .filter(
        ({ spec, status }) =>
          status === ToolStatus.PUBLISHED && spec.name !== undefined,
      );

    return chain(validSpecs)
      .groupBy(({ spec }) => ("toolkit" in spec ? spec.toolkit : NO_TOOLKIT))
      .entries()
      .flatMap(([toolkit, specs]) => {
        const options: EuiSelectableOption[] = [];

        if (toolkit !== NO_TOOLKIT) {
          options.push({
            label: toolkit,
            isGroupLabel: true,
          });
        }

        return [
          ...options,
          ...specs.map(({ toolName, spec, toolVersionId, toolHelp }) => ({
            label: toolName,
            data: { toolName, toolHelp, spec, toolVersionId },
          })),
        ];
      })
      .value();
  }, [availableToolSpecs]);

  const [isOpen, setIsOpen] = useState(autoFocus);
  const [options, setOptions] = useState(initialOptions);
  const [inputValue, setInputValue] = useState(value?.name ?? "");

  // Reset options when tool spec context updates
  useEffect(() => {
    setOptions(initialOptions);
  }, [initialOptions]);

  return (
    <EuiFormRow label={label} fullWidth>
      <EuiSelectable
        aria-label="Select analysis"
        options={options}
        renderOption={(option) => {
          return (
            <span>
              <b>{option.label}</b> {get(option, "spec.version")}
              <br />
              {get(option, "spec.help") ?? get(option, "toolHelp")}
            </span>
          );
        }}
        onChange={(newOptions, event, changedOption) => {
          setOptions(newOptions);
          if (changedOption.checked === "on") {
            setInputValue(changedOption.label);
            onChange(
              get(changedOption, "data.toolName") as string,
              get(changedOption, "data.spec") as ToolSpec,
              get(changedOption, "data.toolVersionId") as number,
            );
            // setIsSearching(false);
            setIsOpen(false);
          } else {
            setInputValue("");
            onChange(undefined, undefined, undefined);
          }
        }}
        singleSelection
        searchable
        searchProps={{
          autoFocus: autoFocus,
          value: inputValue,
          onChange: (value) => {
            setInputValue(value);
            // setIsSearching(true);
          },
          placeholder: "Select an analysis...",
          onKeyDown: (event) => {
            if (event.key === "Tab") return setIsOpen(false);
            if (event.key !== "Escape") return setIsOpen(true);
          },
          onClick: () => setIsOpen(true),
          onFocus: () => setIsOpen(true),
        }}
        listProps={{
          style: { maxHeight: 400, overflow: "scroll" },
          textWrap: "wrap",
          isVirtualized: false,
        }}
      >
        {(list, search) => (
          <EuiInputPopover
            autoFocus={autoFocus}
            fullWidth
            closePopover={() => {
              setIsOpen(false);
              setInputValue(value?.name ?? "");
            }}
            disableFocusTrap
            closeOnScroll
            isOpen={isOpen}
            input={search ?? ""}
            panelPaddingSize="none"
          >
            {list}
          </EuiInputPopover>
        )}
      </EuiSelectable>
    </EuiFormRow>
  );
};
